import { useQueries } from '@tanstack/react-query';
import { FilesService, type TCFile } from 'src/api/generated';

interface UseFilesMetadataResult {
  files: TCFile[];
  loading: boolean;
  error: Error | null;
}

const normalizeFileIds = (fileIds: string[]): string[] => [
  ...new Set(fileIds.map((fileId) => fileId.trim()).filter(Boolean)),
];

const isFile = (value: TCFile | null): value is TCFile => value != null;

/**
 * Loads Files-service metadata for a stable ordered list of file ids.
 *
 * @remarks
 * The query is cached so repeated consumers of the same file set do not issue
 * duplicate requests during the same editor session.
 */
export const useFilesMetadata = (fileIds: string[]): UseFilesMetadataResult => {
  const normalizedFileIds = normalizeFileIds(fileIds);
  const queryResults = useQueries({
    queries: normalizedFileIds.map((fileId) => ({
      queryKey: ['files', 'metadata', fileId],
      queryFn: async () => await FilesService.getFile(fileId),
      staleTime: 60_000,
    })),
  });

  const files = queryResults
    .map((queryResult) => queryResult.data ?? null)
    .filter(isFile);
  const firstError = queryResults.find(
    (queryResult): queryResult is typeof queryResult & { error: Error } =>
      queryResult.error instanceof Error
  );
  const loading =
    normalizedFileIds.length > 0 &&
    queryResults.some(
      (queryResult) => queryResult.isPending && queryResult.data == null
    );

  return {
    files,
    loading,
    error: firstError?.error ?? null,
  };
};
